import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

// ─── Sortable bookmark card wrapper ────────────────────────────────
// Wraps any child (the bookmark card markup) in a dnd-kit sortable
// container, applies the live transform/transition styles, surfaces a
// drag-grip handle in the top-left corner, and renders a chunky #N
// rank badge when `showRank` is true.
//
// Keeping this isolated means the giant card markup downstream stays
// untouched — we just decide per render whether to wrap each card.
//
// `disabled` short-circuits the sortable hook so we don't pay the
// useSortable cost on the (much more common) non-manual-order grids.
export function SortableCard({ id, disabled, rank, showRank, children }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id, disabled });
    const style = disabled
        ? undefined
        : {
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.6 : 1,
            zIndex: isDragging ? 50 : 'auto',
            // Lift the dragged card visually so it tracks the cursor.
            boxShadow: isDragging
                ? '0 12px 32px -8px rgba(16, 185, 129, 0.35), 0 4px 12px -4px rgba(0,0,0,0.18)'
                : undefined,
        };
    return (
        <div ref={setNodeRef} style={style} className="relative">
            {showRank && (
                <span
                    className="absolute -top-2 -left-2 z-20 min-w-[28px] h-7 px-1.5 inline-flex items-center justify-center rounded-full bg-emerald-500 text-white text-xs font-bold tabular-nums shadow-md ring-2 ring-white pointer-events-none"
                    data-testid={`rank-badge-${id}`}
                >
                    #{rank}
                </span>
            )}
            {!disabled && (
                <button
                    type="button"
                    {...attributes}
                    {...listeners}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute top-2 left-2 z-20 w-8 h-8 rounded-full bg-white/95 backdrop-blur shadow-sm flex items-center justify-center text-zinc-700 hover:bg-white active:scale-95 cursor-grab active:cursor-grabbing touch-none"
                    aria-label="Drag to reorder"
                    title="Drag to reorder"
                    data-testid={`drag-handle-${id}`}
                >
                    <GripVertical className="w-4 h-4" />
                </button>
            )}
            {children}
        </div>
    );
}
